// studentapps/Navigation.js
import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Toolbar,
  Divider,
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Code as CodeIcon,
  Checklist as ChecklistIcon,
  Notes as NotesIcon,
  Timer as TimerIcon,
  LibraryBooks as LibraryIcon,
  School as SchoolIcon,
  CorporateFare as CorporateIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon
} from '@mui/icons-material';

const Navigation = ({ currentView, setCurrentView, open, onToggle, user, onSignOut }) => {
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'problem-solving', label: 'Problem Solving', icon: <CodeIcon /> },
    { id: 'todo', label: 'To-Do List', icon: <ChecklistIcon /> },
    { id: 'notes', label: 'Study Notes', icon: <NotesIcon /> },
    { id: 'study-timer', label: 'Study Timer', icon: <TimerIcon /> },
    { id: 'resources', label: 'Resources', icon: <LibraryIcon /> },
  ];

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleSignOut = () => {
    onSignOut();
    handleUserMenuClose();
  };

  const drawer = (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #0a1128 0%, #1a1f3d 100%)',
      color: 'white',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Toolbar sx={{ 
        background: 'rgba(255, 255, 255, 0.05)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SchoolIcon sx={{ fontSize: 32, color: '#ff4d4d' }} />
          <Box>
            <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
              Student Suite
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Learning Platform
            </Typography>
          </Box>
        </Box>
        <IconButton 
          sx={{ color: 'white', display: { sm: 'none' } }}
          onClick={onToggle}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>
      
      {/* User Info Section */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            p: 2, 
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.05)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)'
            }
          }}
          onClick={handleUserMenuOpen}
        >
          <Avatar 
            src={user?.avatar} 
            alt={user?.name}
            sx={{ 
              width: 40, 
              height: 40,
              border: '2px solid #ff4d4d'
            }}
          >
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="600" noWrap>
              {user?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} noWrap>
              {user?.email}
            </Typography>
          </Box>
        </Box>

        {/* Quick Stats */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label="7 Day Streak" 
            size="small" 
            sx={{ 
              background: 'linear-gradient(135deg, #ff4d4d 0%, #8b0000 100%)',
              color: 'white',
              fontSize: '0.7rem'
            }}
          />
          <Chip 
            label="85% Progress" 
            size="small" 
            variant="outlined"
            sx={{ 
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'white',
              fontSize: '0.7rem'
            }}
          />
        </Box>
      </Box>
      
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      
      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, p: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={currentView === item.id}
              onClick={() => setCurrentView(item.id)}
              sx={{
                borderRadius: 2,
                margin: '2px 8px',
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #ff4d4d 0%, #8b0000 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ff4d4d 0%, #8b0000 100%)',
                  },
                },
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {/* Footer with Branding */}
      <Box sx={{ 
        p: 2, 
        background: 'rgba(0, 0, 0, 0.3)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 1,
          justifyContent: 'center'
        }}>
          <CorporateIcon sx={{ fontSize: 16, color: '#ff4d4d' }} />
          <Typography variant="caption" sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            fontWeight: 500
          }}>
            Powered by
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #ff4d4d 0%, #8b0000 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 700,
          fontSize: '1rem'
        }}>
          Zolang Nexora
        </Typography>
        <Typography variant="caption" sx={{ 
          color: 'rgba(255, 255, 255, 0.6)',
          textAlign: 'center',
          display: 'block',
          mt: 0.5
        }}>
          Student Suite v1.0
        </Typography>
      </Box>
    </Box>
  );

  // User Menu
  const userMenu = (
    <Menu
      anchorEl={userMenuAnchor}
      open={Boolean(userMenuAnchor)}
      onClose={handleUserMenuClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 200,
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <MenuItem onClick={handleUserMenuClose}>
        <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
        Profile
      </MenuItem>
      <MenuItem onClick={handleUserMenuClose}>
        <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
        Settings
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
        <LogoutIcon sx={{ mr: 2 }} />
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: 280 }, flexShrink: { sm: 0 } }}
    >
      <Drawer
        variant="temporary"
        open={open}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            border: 'none'
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            border: 'none'
          },
        }}
        open
      >
        {drawer}
      </Drawer>
      
      {userMenu}
    </Box>
  );
};

export default Navigation;